import {
    UNIVERSAL_SEARCH_CATEGORY_ORDER,
    UNIVERSAL_SEARCH_MIN_QUERY_LENGTH,
    UniversalSearchGroup,
    UniversalSearchResultItem,
} from './universal-search.models';

export function normalizeSearchQuery(query: string | null | undefined): string {
    return (query ?? '').trim();
}

export function isSearchQueryReady(query: string | null | undefined): boolean {
    return normalizeSearchQuery(query).length >= UNIVERSAL_SEARCH_MIN_QUERY_LENGTH;
}

export function matchesSearchQuery(
    haystack: string | null | undefined,
    query: string
): boolean {
    const normalizedHaystack = (haystack ?? '').toLowerCase();
    const normalizedQuery = normalizeSearchQuery(query).toLowerCase();
    if (!normalizedQuery) {
        return false;
    }
    return normalizedHaystack.includes(normalizedQuery);
}

export function groupSearchResults(items: UniversalSearchResultItem[]): UniversalSearchGroup[] {
    const byCategory = new Map<string, UniversalSearchResultItem[]>();

    for (const item of items) {
        const category = item.category || 'Other';
        const existing = byCategory.get(category) ?? [];
        existing.push(item);
        byCategory.set(category, existing);
    }

    const orderedCategories = [
        ...UNIVERSAL_SEARCH_CATEGORY_ORDER.filter((c) => byCategory.has(c)),
        ...Array.from(byCategory.keys()).filter(
            (c) => !UNIVERSAL_SEARCH_CATEGORY_ORDER.includes(c)
        ),
    ];

    return orderedCategories.map((category) => ({
        category,
        items: byCategory.get(category) ?? [],
    }));
}

export function flattenGroupedResults(groups: UniversalSearchGroup[]): UniversalSearchResultItem[] {
    return groups.reduce<UniversalSearchResultItem[]>(
        (acc, group) => acc.concat(group.items),
        []
    );
}

export function resolveEnterNavigationTarget(
    groups: UniversalSearchGroup[],
    highlightedIndex: number | null
): UniversalSearchResultItem | null {
    const flat = flattenGroupedResults(groups);
    if (!flat.length) {
        return null;
    }

    if (
        highlightedIndex !== null &&
        highlightedIndex !== undefined &&
        highlightedIndex >= 0 &&
        highlightedIndex < flat.length
    ) {
        return flat[highlightedIndex];
    }

    if (flat.length === 1) {
        return flat[0];
    }

    return null;
}

export function buildHighlightedHtml(text: string, query: string): string {
    const safeText = escapeHtml(text ?? '');
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery) {
        return safeText;
    }

    const escapedQuery = escapeRegExp(escapeHtml(normalizedQuery));
    const regex = new RegExp(`(${escapedQuery})`, 'ig');
    return safeText.replace(regex, '<mark class="universal-search-mark">$1</mark>');
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
